import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { length } from 'ramda'
import useWindowSize from 'hooks/useWindowSize'
import { Icon } from 'components/patterns'
import { ScrollButton } from 'components/PanZoomToolbar/PanZoomToolbar.style'
import { VECHICLE_TYPE_BUS } from 'utils/constants'
import ModuleSummary from './ModuleSummary'
import {
  ModulesContainer,
  ModulesScrollWrapper,
  ModuleCard,
  Controls,
} from './ChecPlusVehicleSummaryPane.style'

import {
  XLARGE,
  GAP_MIN,
  GAP_MAX,
  MIN_WINDOW_WIDTH,
  MODULE_LAYOUT_SIZE,
  MAX_MODULES_TO_DISPLAY,
} from './constants'

const ChecPlusVehicleSummaryModulesContanier = ({
  vehicle,
  vehicleModules,
  selected,
  setSelected,
}) => {
  const { width: windowWidth } = useWindowSize()
  const numModules = length(vehicleModules)

  const [cardSize, containerSize, modulesContainerSize] = useMemo(() => {
    const numGaps = numModules - 1 // we don't want the gap after all cards

    const size = XLARGE

    if (numModules >= MAX_MODULES_TO_DISPLAY) {
      // more then 6 modules changes to slider mode
      const moduleWidth = MODULE_LAYOUT_SIZE[size] // slider mode only SMALL size cards
      const gapWidth = GAP_MIN + 2 // adding 2 just made it work better
      const moduleAndGap = moduleWidth + gapWidth

      const sliderWidth = numModules * (moduleWidth + gapWidth)
      const numModulesCanFitInWidth = Math.floor(windowWidth / moduleAndGap)

      // const modulesContainerWidth = numModulesCanFitInWidth * moduleAndGap + 20 // adding 20 just made it work better
      const modulesContainerWidth = numModulesCanFitInWidth * moduleAndGap + 20 // adding 20 just made it work better

      return [size, sliderWidth, modulesContainerWidth]
    }

    const requiredWrapperSize =
      numModules * MODULE_LAYOUT_SIZE[size] + numGaps * GAP_MAX
    const sliderWidth =
      requiredWrapperSize < MIN_WINDOW_WIDTH
        ? MIN_WINDOW_WIDTH
        : requiredWrapperSize

    return [size, sliderWidth, windowWidth]
  }, [windowWidth, numModules])

  const handleSetSelected = (name) => {
    const target = name === selected ? null : name
    setSelected(target)
  }

  // sliderIndex starts at 1 because it is use in styles math offset calc
  const [sliderIndex, setSliderIndex] = useState(1)

  const handleScrollLeft = () => {
    if (sliderIndex > 1) {
      setSliderIndex(sliderIndex - 1)
    }
  }

  const handleScrollRight = () => {
    if (sliderIndex < numModules - MAX_MODULES_TO_DISPLAY + 1) {
      setSliderIndex(sliderIndex + 1)
    }
  }

  // slider is based on number of modules and container size less then window size
  // if there are 8 modules, but window is larger then required container size for 8 SMALL cards, no slider mode
  const asSlider =
    numModules > MAX_MODULES_TO_DISPLAY && windowWidth < containerSize

  const leftDisabled = sliderIndex === 1
  const rightDisabled = sliderIndex + MAX_MODULES_TO_DISPLAY === numModules + 1
  const modulesContainSwitchIndicators = vehicleModules.find(
    (item) => item.switchIndicatorCount
  )

  return (
    <ModulesContainer asSlider={asSlider} windowWidth={modulesContainerSize}>
      <ModulesScrollWrapper
        width={containerSize}
        asSlider={asSlider}
        cardIndex={sliderIndex}
        cardSize={cardSize}
        numberOfModules={numModules}
      >
        {vehicleModules.map((module) => (
          <ModuleCard
            size={cardSize}
            key={module.uri}
            selected={selected === module.id}
            onClick={() => handleSetSelected(module.id)}
            asSlider={asSlider}
            numberOfModules={numModules}
          >
            <ModuleSummary
              serialNumber={vehicle.id}
              moduleName={module.id}
              isSchoolBus={vehicle.subType === VECHICLE_TYPE_BUS}
              size={cardSize}
              addExtraSpacingInMetrics={
                modulesContainSwitchIndicators && !module.switchIndicatorCount
              }
            />
          </ModuleCard>
        ))}
      </ModulesScrollWrapper>
      {asSlider && (
        <Controls>
          <ScrollButton
            disabled={leftDisabled}
            type="button"
            position="left"
            className="left"
            onClick={handleScrollLeft}
          >
            <Icon icon="left" />
          </ScrollButton>
          <ScrollButton
            disabled={rightDisabled}
            type="button"
            position="right"
            onClick={handleScrollRight}
            className="right"
          >
            <Icon icon="right" />
          </ScrollButton>
        </Controls>
      )}
    </ModulesContainer>
  )
}

ChecPlusVehicleSummaryModulesContanier.propTypes = {
  vehicle: PropTypes.object,
  vehicleModules: PropTypes.array,
  className: PropTypes.string,
  selected: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  setSelected: PropTypes.func,
}

export default ChecPlusVehicleSummaryModulesContanier
